import React, { useState } from 'react'
import {
  Clock,
  CheckCircle2,
  FileText,
  Zap,
  Image as ImageIcon,
  Sparkles,
  Activity as ActivityIcon,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { useActivity } from '../hooks/useActivity'
import { useAuth } from '../hooks/useAuth'
import { getIconComponent } from '../lib/iconMapping'

// Helper function to format relative time
function formatRelativeTime(date: string): string {
  const now = new Date()
  const then = new Date(date)
  const diffInSeconds = Math.floor((now.getTime() - then.getTime()) / 1000)

  if (diffInSeconds < 60) return 'just now'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`
  return then.toLocaleDateString()
}

// Get icon and color based on action type
function getActivityStyle(action: string) {
  const actionLower = action.toLowerCase()

  if (actionLower.includes('ocr') || actionLower.includes('processed')) {
    return { icon: ImageIcon, color: 'text-blue-600 bg-blue-100' }
  }
  if (actionLower.includes('save') || actionLower.includes('upload')) {
    return { icon: FileText, color: 'text-emerald-600 bg-emerald-100' }
  }
  if (actionLower.includes('generate') || actionLower.includes('create')) {
    return { icon: Sparkles, color: 'text-purple-600 bg-purple-100' }
  }
  if (actionLower.includes('complete') || actionLower.includes('success')) {
    return { icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-100' }
  }
  if (actionLower.includes('deploy') || actionLower.includes('publish')) {
    return { icon: Zap, color: 'text-amber-600 bg-amber-100' }
  }

  return { icon: ActivityIcon, color: 'text-slate-600 bg-slate-100' }
}

// Format activity text with tool name and details
function formatActivityText(activity: any): string {
  const toolName = activity.tool_id ? activity.tool_id.toUpperCase() : 'System'
  const action = activity.action.replace(/_/g, ' ')

  // Try to extract meaningful details
  if (activity.details?.file_name) {
    return `${toolName}: ${action} - ${activity.details.file_name}`
  }
  if (activity.details?.word_count) {
    return `${toolName}: ${action} (${activity.details.word_count} words)`
  }

  return `${toolName}: ${action}`
}

export function ActivitySidebar() {
  const { activities, loading, error } = useActivity()
  const { profile } = useAuth()
  const [isAccountExpanded, setIsAccountExpanded] = useState(false)

  // Calculate storage usage (example - you can make this dynamic)
  const storageUsed = profile?.credits_used || 0
  const storageLimit = profile?.credits_limit || 1000
  const storagePercentage = Math.round((storageUsed / storageLimit) * 100)

  return (
    <aside className="w-80 bg-[#e5e7eb] border-l border-slate-300 flex flex-col h-screen fixed right-0 top-0 z-30 hidden xl:flex">
      <div className="h-16 flex items-center justify-between px-6 border-b border-slate-300/50">
        <h2 className="font-semibold text-slate-800 flex items-center">
          <Clock className="mr-2 h-4 w-4 text-slate-500" />
          Recent Activity
        </h2>
        {activities.length > 5 && (
          <span className="text-xs font-medium text-slate-500 bg-white px-2 py-1 rounded-full">
            {activities.length}
          </span>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
            <p className="text-sm text-slate-500">Loading activities...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-red-800">Failed to load</p>
              <p className="text-xs text-red-600 mt-0.5">Please try again later</p>
            </div>
          </div>
        ) : activities.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center mb-2">
              <ActivityIcon className="h-5 w-5 text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-600">No activity yet</p>
            <p className="text-xs text-slate-500 mt-1">
              Start using tools to see your activity
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {activities.slice(0, 5).map((activity) => {
                const style = getActivityStyle(activity.action)
                const ActivityIconComponent = style.icon

                return (
                  <div key={activity.id} className="group">
                    <div className="bg-white p-2.5 rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer">
                      <div className="flex items-start gap-2">
                        <div
                          className={`h-6 w-6 rounded-lg flex items-center justify-center flex-shrink-0 ${style.color}`}
                        >
                          <ActivityIconComponent className="h-3.5 w-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-800 leading-snug line-clamp-2">
                            {formatActivityText(activity)}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-xs text-slate-500">
                              {formatRelativeTime(activity.created_at)}
                            </p>
                            {activity.credits_used > 0 && (
                              <span className="text-xs font-medium text-blue-600">
                                {activity.credits_used} credits
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {activities.length > 5 && (
              <div className="mt-3 text-center">
                <p className="text-xs text-slate-500">
                  +{activities.length - 5} more activities
                </p>
              </div>
            )}
          </>
        )}

        <div className="mt-4 pt-4 border-t border-slate-300">
          <button
            onClick={() => setIsAccountExpanded(!isAccountExpanded)}
            className="w-full flex items-center justify-between text-xs font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-700 transition-colors"
          >
            <span>Account Overview</span>
            {isAccountExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>

          {isAccountExpanded && (
            <div className="mt-3 space-y-2 bg-white rounded-lg p-3 border border-slate-200">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-600">Plan Type</span>
                <span className="font-medium text-slate-800 capitalize">
                  {profile?.plan_type || 'Free'}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-600">Credits Used</span>
                <span className="text-slate-800 font-medium">
                  {storageUsed} / {storageLimit}
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-1.5 mt-2">
                <div
                  className={`h-1.5 rounded-full transition-all ${
                    storagePercentage >= 90
                      ? 'bg-red-500'
                      : storagePercentage >= 70
                      ? 'bg-amber-500'
                      : 'bg-blue-500'
                  }`}
                  style={{ width: `${Math.min(storagePercentage, 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-slate-500">
                {storagePercentage}% of monthly credits used
              </p>
            </div>
          )}
        </div>
      </div>
    </aside>
  )
}